import {css} from '@emotion/react';
import {
    Button,
    ExternalLink,
    FieldError,
    Heading,
    Modal,
    ModalContent,
    Text,
} from '@netapp/bxp-design-system-react';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from 'store';
import {extractErrorMessage} from 'utils/errors';
import {ReactComponent as MigrateWithConfidence} from 'ui-utils/images/welcomeModal/WellArchitectedDashboard.svg';
import {ReactComponent as WellArchitectedWorkloads} from 'ui-utils/images/welcomeModal/AutomaticCapacityManagement.svg';
import operateWithAiOpsIllustration from './images/globalAiFeaturesSplash/operateAtScaleWithAiOps.png';
import {ReactComponent as WelcomeStars} from './images/globalAiFeaturesSplash/welcomeStars.svg';
import {AccountConfigModeType} from 'modules/administration/components/ai-settings/types';
import {
    canManageGlobalAiFeatures,
    useConfigureAccountAi,
    useGlobalAiFeaturesConfig,
    useSetGlobalAiFeaturesConfig,
} from '../globalAiFeaturesQueries';

/**
 * Under BXP the shell renders in a shadow root where the design system stylesheet wins over Emotion,
 * so the Modal/ModalContent overrides have to be inline to beat the default width and padding.
 */
const modalStyle = {width: 900, maxWidth: '100%'} as const;
const modalContentStyle = {padding: 0} as const;

const contentCss = css`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-top: 40px;
`;

const titleCss = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
`;

const heroIconCss = css`
    width: 59px;
    height: 60px;
    flex: none;
`;

const cardsCss = css`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 0 32px;
`;

const cardCss = css`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 20px;
`;

const illustrationCss = css`
    width: 100%;
    height: auto;
    flex: none;
    aspect-ratio: 253 / 133;
`;

const cardCopyCss = css`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const bodyTextCss = css`
    line-height: 24px;
`;

const termsLinkCss = css`
    text-decoration: underline;
    text-underline-position: from-font;
`;

const termsCss = css`
    display: flex;
    min-height: 216px;
    box-sizing: border-box;
    flex-direction: column;
    gap: 16px;
    padding: 24px 32px;
    border-top: 1px solid var(--border-main);
`;

const actionsCss = css`
    display: flex;
    min-height: 96px;
    box-sizing: border-box;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin: 0 40px;
    border-top: 1px solid var(--border-main);
`;

const actionButtonCss = css`
    min-width: 152px;
`;

const testIds = {
    modal: 'global-ai-features-splash',
    accept: 'global-ai-features-accept',
    disable: 'global-ai-features-disable',
} as const;

/** Disabling AI runs two requests, so the spinner follows the clicked button instead of a single mutation. */
type SplashAction = 'accept' | 'disable';

const benefitCards = [
    {
        illustration: <MigrateWithConfidence css={illustrationCss}/>,
        title: 'Migrate with confidence',
        description: 'Simplified migration to the cloud with calculators, advisors and workflows.',
    },
    {
        illustration: <WellArchitectedWorkloads css={illustrationCss}/>,
        title: 'Keep workloads well-architected',
        description: 'Continuously aligned configurations for DB, AI/ML, analytics, file shares, EDA, VMware and more.',
    },
    {
        illustration: <img css={illustrationCss} src={operateWithAiOpsIllustration} alt=""/>,
        title: 'Operate at scale with AIOps',
        description: 'Agent-driven analysis, troubleshooting, and governed automation.',
    },
] as const;

const GlobalAiFeaturesSplash = () => {
    const [pendingAction, setPendingAction] = useState<SplashAction | null>(null);
    const accountId = useSelector((state: RootState) => state.tenancy.selectedAccountId) ?? '';
    const userRole = useSelector((state: RootState) => state.tenancy.selectedAccount?.userRole);
    const isAccountAdmin = canManageGlobalAiFeatures(userRole);
    const configQuery = useGlobalAiFeaturesConfig({accountId, enabled: isAccountAdmin});
    const setConfig = useSetGlobalAiFeaturesConfig({accountId});
    const configureAi = useConfigureAccountAi({accountId});

    const shouldShow = isAccountAdmin
        && configQuery.isSuccess
        && configQuery.data.config.globalAiFeatures === undefined;

    if (!shouldShow) {
        return null;
    }

    const runAction = async (action: SplashAction, request: () => Promise<unknown>) => {
        setPendingAction(action);
        try {
            await request();
        } catch {
            // Mutation state is rendered above the action buttons.
        } finally {
            setPendingAction(null);
        }
    };

    const acceptAndEnable = () => runAction('accept', async () => {
        await configureAi.mutateAsync(AccountConfigModeType.NETAPP_MODEL_DEFAULT);
        await setConfig.mutateAsync(true);
    });
    const disableAllAiFeatures = () => runAction('disable', async () => {
        await configureAi.mutateAsync(AccountConfigModeType.DISABLED);
        await setConfig.mutateAsync(false);
    });

    const error = setConfig.error ?? configureAi.error;

    return (
        <Modal style={modalStyle}>
            <ModalContent style={modalContentStyle}>
                <div css={contentCss} data-testid={testIds.modal}>
                    <div css={titleCss}>
                        <WelcomeStars css={heroIconCss}/>
                        <Heading level="24" bold center>Welcome to Workload Factory</Heading>
                        <Text bold center>
                            FSx for NetApp ONTAP console with guided, best-practice workflows
                        </Text>
                    </div>

                    <div css={cardsCss}>
                        {benefitCards.map(({illustration, title, description}) => (
                            <div css={cardCss} key={title}>
                                {illustration}
                                <div css={cardCopyCss}>
                                    <Heading level="16" bold>{title}</Heading>
                                    <Text level={14} css={bodyTextCss}>{description}</Text>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div css={termsCss}>
                        <Text level={14} css={bodyTextCss}>
                            Workload Factory AIOps uses artificial intelligence to analyze your environment, generate
                            recommendations, and, when authorized by you, perform actions using the credentials and
                            permissions you configure. AI-generated outputs may be inaccurate or incomplete. Actions may
                            modify resources or affect charges in your cloud account.
                        </Text>
                        <Text level={14} css={bodyTextCss}>
                            By selecting <strong>Accept</strong>, you represent that you are authorized to accept these
                            terms on behalf of your organization and agree to the{' '}
                            <ExternalLink
                                css={termsLinkCss}
                                href="https://www.netapp.com/how-to-buy/sales-terms-and-conditions/terms-with-customers/general-terms/general-terms/"
                                variant="text"
                            >
                                NetApp General Terms
                            </ExternalLink>
                            , including the AI-related terms applicable to this feature.
                        </Text>
                        <Text color="text-secondary" level={14} css={bodyTextCss}>
                            You can change your preferences at any time in the administration/AI-settings section.
                        </Text>
                    </div>

                    <div css={actionsCss}>
                        {error && (
                            <FieldError
                                message={extractErrorMessage(error)}
                                type="error"
                                dataTestId={`${testIds.modal}-error`}
                            />
                        )}
                        <Button
                            css={actionButtonCss}
                            color="primary"
                            dataTestId={testIds.accept}
                            disabled={pendingAction !== null}
                            isSubmitting={pendingAction === 'accept'}
                            onClick={acceptAndEnable}
                        >
                            Accept
                        </Button>
                        <Button
                            css={actionButtonCss}
                            color="secondary"
                            dataTestId={testIds.disable}
                            disabled={pendingAction !== null}
                            isSubmitting={pendingAction === 'disable'}
                            onClick={disableAllAiFeatures}
                        >
                            Disable all AI features
                        </Button>
                    </div>
                </div>
            </ModalContent>
        </Modal>
    );
};

export default GlobalAiFeaturesSplash;
